<template>
    <kt-modal
        :visible="true"
        :title="recordTitle"
        size="modal-xl"
        :dismissable="false"
    >
        <template #body>
            <work-order-form ref="workOrderFormRef" v-model="record"></work-order-form>
        </template>
    
        <template #footer>
            <div class="kt-modal-actions d-flex align-items-center justify-content-between w-100">
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="sendNotificationToggleWOEmbed"
                        v-model="sendNotification"
                    >
                    <label class="form-check-label" for="sendNotificationToggleWOEmbed">
                        Send Update Notification
                    </label>
                </div>
                <div>
                    <button @click="handleClose()" class="btn btn-outline-secondary">
                        <X :size="16" class="me-1" />Close
                    </button>
                    <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger ms-2">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                    <button @click="saveRecord()" class="btn btn-primary ms-2">
                        <Save :size="16" class="me-1" />Save
                    </button>
                </div>
            </div>
        </template>
        
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { WorkOrderModel } from "@/models/WorkOrderModel";
import Api from "@/services/ApiService";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import Emitter from "@/services/Emitter";
import KtModal from "@/components/UI/KtModal.vue";
import WorkOrderForm from "@/components/WorkOrders/WorkOrderForm.vue";
import { X, Trash2, Save } from 'lucide-vue-next';

const props = defineProps({
    recordId: {
        type: String,
        required: true
    },
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    }
});

const emit = defineEmits(['close']);

const recordTitle = ref("Client Document");
const sendNotification = ref(false);
const workOrderFormRef = ref(null);

const newRecord = () => {
    const rec = new WorkOrderModel();
    // Pre-populate with requestId if provided
    if (props.requestId) {
        rec.RequestId = Number(props.requestId);
    }
    // Pre-populate with clientId if provided
    if (props.clientId) {
        rec.ContactId = Number(props.clientId);
    }
    return rec;
};

const record = ref<WorkOrderModel>(newRecord());

const saveRecord = async () => {
    try {
        console.log('[WorkOrderEditEmbedded] saveRecord called');
        console.log('[WorkOrderEditEmbedded] workOrderFormRef.value:', workOrderFormRef.value);
        console.log('[WorkOrderEditEmbedded] hasUnsavedItems method exists?', workOrderFormRef.value?.hasUnsavedItems);
        
        // Check for unsaved line items before saving
        if (workOrderFormRef.value && workOrderFormRef.value.hasUnsavedItems && workOrderFormRef.value.hasUnsavedItems()) {
            console.log('[WorkOrderEditEmbedded] BLOCKING SAVE - unsaved items detected');
            Alert.notify({ 
                text: "You have unsaved line items. Please save or cancel them before saving the invoice.", 
                type: "warning" 
            });
            return;
        }
        
        console.log('[WorkOrderEditEmbedded] No unsaved items, proceeding with save');

        // Validate
        if (!record.value.ContactId) {
            Alert.notify({ text: "Please select a client", type: "error" });
            return;
        }

        // Auto-generate invoice number if not provided
        if (!record.value.InvoiceNumber && record.value.Contact && record.value.Id) {
            const contactCode = record.value.Contact.Code || 'INV';
            record.value.InvoiceNumber = `${contactCode}-${record.value.Id}`;
        }

        // Save
        const saved = await Api.saveWorkOrder(record.value);
        
        // Auto-generate invoice number for new records (after first save)
        if (!saved.InvoiceNumber && saved.Contact && saved.Id) {
            const contactCode = saved.Contact.Code || 'INV';
            saved.InvoiceNumber = `${contactCode}-${saved.Id}`;
            // Save again with the generated invoice number
            await Api.saveWorkOrder(saved);
        }
        
        // Reload the full work order with all relationships
        if (saved.Id) {
            const fullRecord = await Api.loadWorkOrder(saved.Id);
            record.value = fullRecord;
        } else {
            record.value = saved;
        }
        
        Toast.success("Work order saved successfully");
        
        // Refresh datatable
        Emitter.emit('datatable-refresh', 'workorder-embedded');
        
        // Close modal after save
        emit('close');
    } catch (error) {
        console.error("Error saving work order:", error);
        Alert.notify({ text: "Failed to save work order", type: "error" });
    }
};

const deleteRecord = () => {
    Alert.confirm(
        'Are you sure you want to delete this work order?',
        async () => {
            try {
                await Api.deleteWorkOrder(record.value.Id!);
                Toast.success("Work order deleted");
                
                // Refresh datatable
                Emitter.emit('datatable-refresh', 'workorder-embedded');
                
                emit('close');
            } catch (error) {
                console.error("Error deleting work order:", error);
                Alert.notify({ text: "Failed to delete work order", type: "error" });
            }
        },
        () => {} // No action on cancel
    );
};

const handleClose = () => {
    console.log('[WorkOrderEditEmbedded] handleClose called');
    console.log('[WorkOrderEditEmbedded] workOrderFormRef.value:', workOrderFormRef.value);
    console.log('[WorkOrderEditEmbedded] hasUnsavedItems method exists?', workOrderFormRef.value?.hasUnsavedItems);
    
    // Check if there are unsaved line items
    if (workOrderFormRef.value && workOrderFormRef.value.hasUnsavedItems && workOrderFormRef.value.hasUnsavedItems()) {
        console.log('[WorkOrderEditEmbedded] BLOCKING CLOSE - unsaved items detected');
        Alert.confirm(
            'You have unsaved line items. If you close now, those changes will be lost. Are you sure you want to close?',
            () => {
                console.log('[WorkOrderEditEmbedded] User confirmed close despite unsaved items');
                emit('close');
            },
            () => {
                console.log('[WorkOrderEditEmbedded] User cancelled close');
                // Do nothing - modal stays open
            }
        );
    } else {
        console.log('[WorkOrderEditEmbedded] No unsaved items, closing normally');
        emit('close');
    }
};

const loadRecord = async (val: string) => {
    switch (val) {
        case "new":
            recordTitle.value = "New Invoice"; // New Client Document
            record.value = newRecord();
            break;

        default:
            try {
                const data = await Api.loadWorkOrder(Number(val));
                record.value = data;
                const type = data?.Title || 'Invoice'; // Invoice
                recordTitle.value = `${type} #${data.InvoiceNumber || data.Id}`;
            } catch (error) {
                console.error("Error loading record:", error);
                Alert.notify({ text: "Failed to load record", type: "error" });
            }
            break;
    }
};

// Load record when component mounts or recordId changes
watch(() => props.recordId, (newId) => {
    if (newId) {
        loadRecord(newId);
    }
}, { immediate: true });
</script>
